import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getAuth,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  multiFactor,
} from 'firebase/auth';
import '../firebase';

export const firebaseAuth = React.createContext();

const ANON_USER = {};

// Bump this constant whenever the auth flow changes in a way that requires
// existing users to re-authenticate (e.g., MFA rollout, claim shape change).
// On boot, if the value stored in localStorage doesn't match, we sign out any
// restored session once to clear stale Firebase IndexedDB persistence from
// older deploys before treating it as valid.
const AUTH_SCHEMA_KEY = 'pcna-auth-schema';
const AUTH_SCHEMA_VERSION = '2026-05-mfa';

const computeMfaEnrolled = (fbUser) => {
  if (!fbUser) return false;
  try {
    const factors = multiFactor(fbUser).enrolledFactors;
    return Array.isArray(factors) && factors.length > 0;
  } catch {
    return false;
  }
};

const buildUserShape = (fbUser, idTokenResult) => ({
  isAuthenticated: true,
  email: fbUser.email,
  role: idTokenResult.claims.role,
  crmManager: Number(idTokenResult.claims.role) !== 7 && idTokenResult.claims.crmManager === true,
  token: idTokenResult.token,
  emailVerified: !!fbUser.emailVerified,
  multiFactor: !!idTokenResult.signInSecondFactor || computeMfaEnrolled(fbUser),
  user: fbUser,
});

const AuthProvider = (props) => {
  const auth = getAuth();
  const [inputs, setInputs] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState([]);
  const [user, setUser] = useState(ANON_USER);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(AUTH_SCHEMA_KEY);
      if (stored !== AUTH_SCHEMA_VERSION) {
        window.localStorage.setItem(AUTH_SCHEMA_KEY, AUTH_SCHEMA_VERSION);
        fbSignOut(auth).catch(() => {});
      }
    } catch {
      // localStorage unavailable (e.g., privacy mode); skip.
    }
  }, [auth]);

  useEffect(() => {
    let isFirstFire = true;
    const unsubscribe = onIdTokenChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setUser(ANON_USER);
        setLoading(false);
        isFirstFire = false;
        return;
      }
      // On the first hydration of a restored session, force-refresh the token
      // against the server. If the cached refresh token is revoked/expired,
      // this throws and we sign out cleanly instead of showing a fake-
      // authenticated UI that gets kicked out ~30s later when the SDK refreshes
      // on its own.
      const forceRefresh = isFirstFire;
      isFirstFire = false;
      try {
        const idTokenResult = await fbUser.getIdTokenResult(forceRefresh);
        setUser(buildUserShape(fbUser, idTokenResult));
      } catch (err) {
        console.error('ID token validation failed, signing out', err);
        try {
          await fbSignOut(auth);
        } catch {
          // ignore — listener will fire with null and reset state
        }
        setUser(ANON_USER);
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [auth]);

  const handleSignin = useCallback(async () => {
    try {
      await signInWithEmailAndPassword(auth, inputs.email, inputs.password);
    } catch (err) {
      setErrors((prev) => [...prev, err.message]);
    }
  }, [auth, inputs.email, inputs.password]);

  const handleSignout = useCallback(async () => {
    try {
      await fbSignOut(auth);
    } catch (err) {
      console.error('Sign out failed', err);
    }
    setUser(ANON_USER);
  }, [auth]);

  const refreshClaims = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const idTokenResult = await auth.currentUser.getIdTokenResult(true);
      setUser(buildUserShape(auth.currentUser, idTokenResult));
    } catch (err) {
      console.error('Failed to refresh claims', err);
    }
  }, [auth]);

  const value = useMemo(
    () => ({
      user,
      loading,
      inputs,
      setInputs,
      errors,
      setErrors,
      handleSignin,
      handleSignout,
      refreshClaims,
    }),
    [user, loading, inputs, errors, handleSignin, handleSignout, refreshClaims]
  );

  return (
    <firebaseAuth.Provider value={value}>{props.children}</firebaseAuth.Provider>
  );
};

export default AuthProvider;
